/**
 * Phone navigation (D-106).
 *
 * The desktop shell shows the five dashboard categories as a tab bar and keeps the
 * account actions in the top bar. On a phone both are too far from the thumb, so the
 * app grows a fixed bottom bar: up to four category shortcuts plus a **More** sheet
 * that carries the rest, the settings pages, the theme switch and Sign out.
 *
 * What a person may see is decided exactly as everywhere else — by the backend
 * capability catalogue (`hasCapability`), never by a role name. A shift leader
 * therefore gets Overview + Modules, while a general manager gets four categories in
 * the bar and Tools inside the sheet.
 */
import { hasCapability } from '../context/AuthContext'
import { DASHBOARD_CATEGORY_PATHS, type DashboardCategoryKey } from './dashboardRoutes'
import type { AuthUser } from '../types/identity'

export interface MobileDestination {
  key: DashboardCategoryKey
  /** The label the dashboard tab bar uses. */
  label: string
  /** A shorter label that fits a bottom-bar slot on a 360 px screen. */
  shortLabel: string
  path: string
  /** Backend capability required to open it. */
  permission: string
}

/** Order matters: the first slots of the bar are the most-used screens. */
export const MOBILE_DESTINATIONS: readonly MobileDestination[] = [
  {
    key: 'overview',
    label: 'General Overview',
    shortLabel: 'Overview',
    path: DASHBOARD_CATEGORY_PATHS.overview,
    permission: 'overview.view',
  },
  {
    key: 'insights',
    label: 'Insights',
    shortLabel: 'Insights',
    path: DASHBOARD_CATEGORY_PATHS.insights,
    permission: 'insights.view',
  },
  {
    key: 'suppliers',
    label: 'Suppliers',
    shortLabel: 'Suppliers',
    path: DASHBOARD_CATEGORY_PATHS.suppliers,
    permission: 'suppliers.view',
  },
  {
    key: 'modules',
    label: 'Modules',
    shortLabel: 'Modules',
    path: DASHBOARD_CATEGORY_PATHS.modules,
    permission: 'modules.view',
  },
  {
    key: 'tools',
    label: 'Tools',
    shortLabel: 'Tools',
    path: DASHBOARD_CATEGORY_PATHS.tools,
    permission: 'tools.view',
  },
]

/** Destinations the bottom bar shows before the rest move into the More sheet. */
export const MOBILE_PRIMARY_SLOTS = 4

/** Every category this account may open, in bar order. */
export function mobileDestinations(user: AuthUser | null): MobileDestination[] {
  return MOBILE_DESTINATIONS.filter((destination) =>
    hasCapability(user, destination.permission),
  )
}

export interface MobileNavSections {
  /** Rendered directly in the bottom bar. */
  primary: MobileDestination[]
  /** Rendered in the More sheet, above the account actions. */
  overflow: MobileDestination[]
}

/**
 * Split the reachable categories between the bar and the More sheet.
 *
 * The bar is never left without a destination when the account may see any screen at
 * all, and the split is stable: the same account always sees the same four slots.
 */
export function mobileNavSections(
  user: AuthUser | null,
  primarySlots: number = MOBILE_PRIMARY_SLOTS,
): MobileNavSections {
  const destinations = mobileDestinations(user)
  const slots = Math.max(1, primarySlots)
  return {
    primary: destinations.slice(0, slots),
    overflow: destinations.slice(slots),
  }
}

/**
 * Which bottom-bar slot the current URL belongs to.
 *
 * Child screens keep their parent lit — `/insights/four-wall-analysis` highlights
 * Insights — and settings pages highlight nothing, because they live in More.
 */
export function activeDestinationKey(path: string): DashboardCategoryKey | null {
  const [first] = path.split('/').filter(Boolean)
  if (!first) return null
  const match = MOBILE_DESTINATIONS.find(
    (destination) => destination.path === `/${first}`,
  )
  return match?.key ?? null
}
